import ErrorPage from 'next/error'
import { useRouter } from 'next/router'

import NavList from '@/components/NavList'
import SanityPreview from '@/components/SanityPreview'
import SEO from '@/components/SEO'
import { Video } from '@/components/Video'
import useColor from '@/hooks/useColor'
import { buildGetStaticPaths, buildGetStaticProps } from '@/lib/page'
import { videoBySlugQuery } from '@/queries/video'
import type { VideoData } from '@/queries/fragments/video'
import type { VideoBySlugQueryResult } from '../../../sanity.types'

const query = videoBySlugQuery

// TypeGen can't see the interpolated ${videoFields}, so add VideoData by hand
type VideoPageData = NonNullable<VideoBySlugQueryResult> & VideoData

export default function Page({
  preview,
  pageData,
  error,
  slug,
}: SanityPageProps<VideoPageData, { slug: string }>) {
  const router = useRouter()
  useColor(null)

  if (error) {
    return <ErrorPage statusCode={error.statusCode} title={error.title} />
  }

  if (!router.isFallback && !pageData) {
    return <ErrorPage statusCode={404} />
  }

  const imageUrl =
    (pageData?.playbackId &&
      `https://image.mux.com/${pageData.playbackId}/thumbnail.jpg?width=1200&height=630&fit_mode=pad&time=0`) ||
    (pageData?.youtubeId && `https://img.youtube.com/vi/${pageData.youtubeId}/maxresdefault.jpg`) ||
    undefined

  return (
    <SanityPreview preview={preview} initialData={pageData} query={query} params={{ slug }}>
      {(video) => {
        return (
          <div>
            <SEO
              title={video?.title ?? undefined}
              path={`video/${slug}`}
              image={imageUrl}
              excludeFromSearch={video?.excludeFromSearch}
            />
            <NavList back={{ title: 'Hjem', href: '/' }} />
            {video ? <Video {...video} /> : <p>No video found.</p>}
          </div>
        )
      }}
    </SanityPreview>
  )
}

export const getStaticProps = buildGetStaticProps<VideoBySlugQueryResult>(videoBySlugQuery)

export const getStaticPaths = buildGetStaticPaths('video')
